import React from 'react';
import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Sidebar } from './components/Sidebar';
import { ChannelProvider } from './context/ChannelContext';
import { AuthProvider } from './context/AuthContext';
import { WorkspaceProvider, useWorkspace } from './context/WorkspaceContext';
import ProtectedRoute from './components/ProtectedRoute';
import { Dashboard, MenuBuilder, Channels, Settings, Login, Register, UserManagement, ActivityLogs, Help, InviteAccept, AdminDashboard, AllUsers, ChannelDetail, Campaigns, CampaignDetail } from './pages';
import WorkspaceManagement from './pages/WorkspaceManagement';
import { usePostHogIdentify } from './context/PostHogContext';
import './App.css';

function AppLayout({ children }) {
  const { selectedWorkspaceId } = useWorkspace();
  usePostHogIdentify();
  return (
    <ChannelProvider workspaceId={selectedWorkspaceId}>
      <div className="app-layout">
        <Sidebar />
        <main className="main-content pt-14 lg:pt-0">
          {children}
        </main>
      </div>
    </ChannelProvider>
  );
}

// Root of every route: providers sit inside the router so they can navigate, and the data router
// lets the menu editor block navigation while it has unsaved changes (useBlocker).
function RootLayout() {
  return (
    <AuthProvider>
      <WorkspaceProvider>
        <Outlet />
        <Toaster
          position="top-right"
          theme="dark"
          toastOptions={{
            style: {
              background: '#18181B',
              border: '1px solid #27272A',
              color: '#FAFAFA'
            }
          }}
        />
      </WorkspaceProvider>
    </AuthProvider>
  );
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/invite/:token" element={<InviteAccept />} />

      {/* Protected routes */}
      <Route path="/" element={<Navigate to="/menus" replace />} />
      <Route path="/dashboard" element={<Navigate to="/menus" replace />} />
      <Route
        path="/menus"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/create"
        element={
          <ProtectedRoute>
            <AppLayout>
              <MenuBuilder />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/edit/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <MenuBuilder />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Campaigns />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/:campaignId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CampaignDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/channels"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Channels />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/channels/:channelId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ChannelDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Settings />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <AppLayout>
              <AdminDashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute requireWorkspaceAdmin>
            <AppLayout>
              <UserManagement />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/logs"
        element={
          <ProtectedRoute requireWorkspaceAdmin>
            <AppLayout>
              <ActivityLogs />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workspaces"
        element={
          <ProtectedRoute requireAdmin>
            <AppLayout>
              <WorkspaceManagement />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/all-users"
        element={
          <ProtectedRoute requireAdmin>
            <AppLayout>
              <AllUsers />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Help />
            </AppLayout>
          </ProtectedRoute>
        }
      />
    </Route>
  )
);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
